import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface GameState {
  level: number;
  score: number;
  lives: number;
  currentCircuit: string;
  completedLevels: number;
  isGameOver: boolean;
  isVictory: boolean;
  ledSequence: number[];
  userSequence: number[];
  isShowingSequence: boolean;
  gameStarted: boolean;
}

const ArduinoLEDGame: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>({
    level: 1,
    score: 0,
    lives: 3,
    currentCircuit: 'basic',
    completedLevels: 0,
    isGameOver: false,
    isVictory: false,
    ledSequence: [],
    userSequence: [],
    isShowingSequence: false,
    gameStarted: false
  });

  const [gameMessage, setGameMessage] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);

  const circuits = {
    basic: {
      name: 'Basic LED Circuit',
      description: 'Learn to control a single LED with Arduino',
      leds: ['Red'],
      sequence: [0],
      code: 'digitalWrite(LED_PIN, HIGH);'
    },
    traffic: {
      name: 'Traffic Light',
      description: 'Create a traffic light sequence with 3 LEDs',
      leds: ['Red', 'Yellow', 'Green'],
      sequence: [0, 1, 2],
      code: '// Traffic light sequence\nredLED.on();\nyellowLED.on();\ngreenLED.on();'
    },
    rainbow: {
      name: 'Rainbow LEDs',
      description: 'Control multiple colored LEDs in sequence',
      leds: ['Red', 'Orange', 'Yellow', 'Green', 'Blue', 'Purple'],
      sequence: [0, 1, 2, 3, 4, 5],
      code: '// Rainbow sequence\nfor(int i = 0; i < 6; i++) {\n  digitalWrite(ledPins[i], HIGH);\n  delay(200);\n}'
    },
    pattern: {
      name: 'Pattern Recognition',
      description: 'Create complex LED patterns',
      leds: ['LED1', 'LED2', 'LED3', 'LED4'],
      sequence: [0, 1, 0, 2, 1, 3],
      code: '// Pattern sequence\nint pattern[] = {0,1,0,2,1,3};\nfor(int i = 0; i < 6; i++) {\n  digitalWrite(ledPins[pattern[i]], HIGH);\n}'
    }
  };

  const generateSequence = (level: number) => {
    const circuit = circuits[gameState.currentCircuit as keyof typeof circuits];
    const sequenceLength = Math.min(3 + level, circuit.leds.length * 2);
    const sequence = [];
    
    for (let i = 0; i < sequenceLength; i++) {
      sequence.push(Math.floor(Math.random() * circuit.leds.length));
    }
    
    return sequence;
  };

  const startNewLevel = useCallback(() => {
    const sequence = generateSequence(gameState.level);
    setGameState(prev => ({
      ...prev,
      ledSequence: sequence,
      userSequence: [],
      isShowingSequence: true,
      gameStarted: true
    }));
    
    // Show sequence with delays
    let index = 0;
    const showNext = () => {
      if (index < sequence.length) {
        setGameMessage(`Watch: LED ${sequence[index]} lights up!`);
        setTimeout(() => {
          setGameMessage('');
          index++;
          setTimeout(showNext, 500);
        }, 1000);
      } else {
        setGameState(prev => ({ ...prev, isShowingSequence: false }));
        setGameMessage('Now repeat the sequence by clicking the LEDs!');
      }
    };
    
    setTimeout(showNext, 1000);
  }, [gameState.level, gameState.currentCircuit]);

  const handleLEDClick = (ledIndex: number) => {
    if (gameState.isShowingSequence || gameState.isGameOver || gameState.isVictory) return;

    const newUserSequence = [...gameState.userSequence, ledIndex];
    setGameState(prev => ({ ...prev, userSequence: newUserSequence }));

    // Check if the sequence is correct so far
    const currentIndex = newUserSequence.length - 1;
    if (newUserSequence[currentIndex] !== gameState.ledSequence[currentIndex]) {
      setGameMessage('❌ Wrong LED! Try again.');
      setGameState(prev => ({ 
        ...prev, 
        lives: prev.lives - 1,
        userSequence: []
      }));
      
      if (gameState.lives <= 1) {
        setTimeout(() => {
          setGameState(prev => ({ ...prev, isGameOver: true }));
        }, 2000);
      } else {
        setTimeout(() => {
          setGameMessage('');
          startNewLevel();
        }, 2000);
      }
      return;
    }

    // Check if sequence is complete
    if (newUserSequence.length === gameState.ledSequence.length) {
      setGameMessage('🎉 Perfect! Level completed!');
      setGameState(prev => ({
        ...prev,
        score: prev.score + 100 * gameState.level,
        completedLevels: prev.completedLevels + 1,
        level: prev.level + 1
      }));

      setTimeout(() => {
        if (gameState.level >= 4) {
          setGameState(prev => ({ ...prev, isVictory: true }));
        } else {
          // Move to next circuit
          const circuitNames = Object.keys(circuits);
          const currentIndex = circuitNames.indexOf(gameState.currentCircuit);
          const nextCircuit = circuitNames[currentIndex + 1] || circuitNames[0];
          setGameState(prev => ({ ...prev, currentCircuit: nextCircuit }));
          setGameMessage('');
          startNewLevel();
        }
      }, 2000);
    }
  };

  const resetGame = () => {
    setGameState({
      level: 1,
      score: 0,
      lives: 3,
      currentCircuit: 'basic',
      completedLevels: 0,
      isGameOver: false,
      isVictory: false,
      ledSequence: [],
      userSequence: [],
      isShowingSequence: false,
      gameStarted: false
    });
    setGameMessage('');
    setShowInstructions(true);
  };

  const getCurrentCircuit = () => {
    return circuits[gameState.currentCircuit as keyof typeof circuits];
  };

  if (showInstructions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl border border-white/20">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🔌</div>
            <h1 className="text-4xl font-bold text-white mb-4">Arduino LED Challenge</h1>
            <p className="text-xl text-white/80">
              Learn Arduino programming by controlling LEDs in this interactive game!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">🎯 How to Play</h3>
              <ul className="space-y-2 text-white/80">
                <li>• Watch the LED sequence carefully</li>
                <li>• Click the LEDs in the same order</li>
                <li>• Complete all levels to win</li>
                <li>• Learn Arduino programming concepts</li>
              </ul>
            </div>

            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">📚 Learning Goals</h3>
              <ul className="space-y-2 text-white/80">
                <li>• Understand digital I/O</li>
                <li>• Learn LED control</li>
                <li>• Practice timing and sequences</li>
                <li>• Develop logical thinking</li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowInstructions(false)}
              className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors mr-4"
            >
              Start Game
            </button>
            <Link
              to="/high-code/games"
              className="bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (gameState.isVictory) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Arduino Master!</h1>
          <p className="text-xl text-gray-700 mb-6">
            Congratulations! You've mastered Arduino LED programming!
          </p>
          <div className="bg-yellow-100 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-2">Final Score: {gameState.score}</h3>
            <p className="text-gray-700">Levels Completed: {gameState.completedLevels}</p>
            <p className="text-gray-700">Circuits Mastered: {Object.keys(circuits).length}</p>
          </div>
          <div className="space-y-4">
            <button
              onClick={resetGame}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors mr-4"
            >
              Play Again
            </button>
            <Link
              to="/high-code/games"
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (gameState.isGameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-gray-800 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl text-center">
          <div className="text-6xl mb-4">💥</div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Circuit Overload!</h1>
          <p className="text-xl text-gray-700 mb-6">
            The circuit got too hot! But don't worry - every engineer learns from mistakes!
          </p>
          <div className="bg-red-100 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-2">Final Score: {gameState.score}</h3>
            <p className="text-gray-700">Levels Completed: {gameState.completedLevels}</p>
          </div>
          <div className="space-y-4">
            <button
              onClick={resetGame}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors mr-4"
            >
              Try Again
            </button>
            <Link
              to="/high-code/games"
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentCircuit = getCurrentCircuit();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Game Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mt-24">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold text-white">🔌 Arduino LED Challenge</h1>
              <div className="flex space-x-4 text-white">
                <div className="bg-blue-600 px-3 py-1 rounded-full">
                  Score: {gameState.score}
                </div>
                <div className="bg-red-600 px-3 py-1 rounded-full">
                  Lives: {gameState.lives}
                </div>
                <div className="bg-green-600 px-3 py-1 rounded-full">
                  Level: {gameState.level}
                </div>
              </div>
            </div>
            <Link
              to="/high-code/games"
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back to Games
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4">
                {currentCircuit.name}
              </h2>
              <p className="text-white/80 mb-6">
                {currentCircuit.description}
              </p>

              {/* Arduino Board */}
              <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-600">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Arduino Uno</h3>
                </div>
                
                {/* LED Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {currentCircuit.leds.map((led, index) => {
                    const isActive = gameState.userSequence.includes(index) || 
                                   (gameState.isShowingSequence && gameState.ledSequence.includes(index));
                    return (
                      <button
                        key={index}
                        onClick={() => handleLEDClick(index)}
                        disabled={gameState.isShowingSequence || gameState.isGameOver || gameState.isVictory}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          isActive
                            ? 'bg-yellow-400 border-yellow-300 shadow-lg shadow-yellow-400/50'
                            : 'bg-gray-700 border-gray-500 hover:bg-gray-600'
                        } ${gameState.isShowingSequence ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="text-center">
                          <div className={`w-8 h-8 rounded-full mx-auto mb-2 ${
                            isActive ? 'bg-yellow-300' : 'bg-gray-500'
                          }`}></div>
                          <p className="text-white text-sm font-medium">{led}</p>
                          <p className="text-gray-400 text-xs">Pin {index + 2}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Game Message */}
              {gameMessage && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 mb-4">
                  <p className="text-blue-200 text-center">{gameMessage}</p>
                </div>
              )}

              {/* Start Button */}
              {!gameState.gameStarted && (
                <div className="text-center">
                  <button
                    onClick={startNewLevel}
                    className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Start Level {gameState.level}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Circuit Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">🔧 Circuit Info</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-white/80 text-sm">Current Circuit:</p>
                  <p className="text-white font-semibold">{currentCircuit.name}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">LEDs:</p>
                  <p className="text-white">{currentCircuit.leds.length}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Sequence Length:</p>
                  <p className="text-white">{gameState.ledSequence.length}</p>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">📊 Progress</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Levels</span>
                    <span>{gameState.completedLevels}/4</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(gameState.completedLevels / 4) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Score</span>
                    <span>{gameState.score}</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-white mb-1">
                    <span>Lives</span>
                    <span>{gameState.lives}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Arduino Code */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">💻 Arduino Code</h3>
              <div className="bg-black/50 rounded-lg p-4 border border-gray-600">
                <pre className="text-green-400 text-sm overflow-x-auto">
                  <code>{currentCircuit.code}</code>
                </pre>
              </div>
            </div>

            {/* Learning Tips */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">💡 Learning Tips</h3>
              <div className="space-y-2 text-white/80 text-sm">
                <p>• Watch the sequence carefully</p>
                <p>• Remember the order of LED activations</p>
                <p>• Practice makes perfect!</p>
                <p>• Learn Arduino programming concepts</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArduinoLEDGame;
